import React, { useEffect, useMemo, useState } from "react";
import styles from "./seekAsset.module.scss";
import { Image } from "react-bootstrap";
import cx from "classnames";
import _ from "lodash";
import {
  fetchAnswerForSeek,
  fetchAssetSummary,
  fetchSeekLimit,
  invalidateSeekSession,
} from "src/redux/actions/seek";
import { useDispatch, useSelector } from "react-redux";
import SeekChat from "../chat";
import Actions from "src/components/actionbar";
import SeekHeader from "../seekHeader";
import { fetchAssetPrompts } from "src/redux/actions/assets";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import { FileService } from "src/utils/file.service";
import { ASSET_DETAIL } from "src/constants/adoption/sourceTypes";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";

interface SeekProps {
  onClose: () => void;
  assetId: string;
}

const SeekAsset: React.FC<SeekProps> = ({ onClose, assetId }) => {
  const dispatch = useDispatch();

  const [isActive, setIsActive] = useState(false);
  const [promptsView, setPromptsView] = useState(true);
  const [query, setQuery] = useState("");
  const [prompts, setPrompts] = useState([]);

  const session = useSelector((state: any) => state.seek.session);
  const chats = useSelector((state: any) => state.seek.session.item.chats);
  const asset = useSelector((state: any) => state?.asset?.item);
  let seekPrompts = useSelector((state: any) =>
    _.get(state, "asset.seekPrompts.prompts", []),
  ); 

  const isSeeking = useSelector((state: any) =>
    _.get(state, "seek.session.loading", false),
  );

  const fileType = useMemo(() => {
    return FileService.getFileType(_.get(asset,"metadata",""));
  },[asset])

  const { trackSeekAssetSummariseClickedEvent, trackAssetPromptsClickedEvent } = useSeekAdoption();
  const { trackContentSearched } = useContentMapAdoption();

  useEffect(() => {
    dispatch(fetchSeekLimit());
    dispatch(fetchAssetPrompts(assetId));
    invalidateSeek();
    setIsActive(true);
  }, []);

  useEffect(() => {
    if(seekPrompts.length > 0) {
      seekPrompts = ["Summarize this asset...", ...seekPrompts];
      setPrompts(seekPrompts);
    }
  }, [seekPrompts]);

  const generateRandomId = () => {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  };

  const tempId = generateRandomId();

  const handleQuery = (prompt: string) => {
    setPromptsView(false);
    dispatch(
      fetchAnswerForSeek({
        id: tempId,
        question: prompt,
        session: session.item.id,
        request: {
            content: assetId ? {
              type: 'asset',
              id: assetId,
            } : null,
            includeActions: !assetId
        }
      }),
    );

    trackContentSearched({
      sourceCta: 'Seek',
      searchString: prompt,
      filter: null,
      searchTags: [],
      categories: []
    });

    setQuery("");
  };

  const handleAssetSummaryClicked = () => {
    setPromptsView(false);
    const data = {
      question: prompts[0],
      session: session?.item?.id,
      querying: true,
      content: {
        type: "asset" as "asset",
        id: assetId
      }
    };

    dispatch(fetchAssetSummary(data));
    setQuery("");
  };

  const invalidateSeek = () => {
    dispatch(invalidateSeekSession());
  };

  const renderPlaceholder = () => {
    if (isSeeking) {
      return "Generating response...";
    } else if(chats.length > 0) {
      return "Ask a follow up question...";
    } else {
      return "Try asking something...";
    }
  };

  return (
    <div className={cx(styles.seekContainer, styles.seekView)}>
      <div className={styles.verticalLine}></div>
      <div className={styles.seekHeader}>
        <SeekHeader />
      </div>
      {promptsView && (
        <div className={styles.seekPrompts}>
          {_.size(prompts) > 1 &&
            prompts.map((prompt: any, index: number) => (
              <div
                key={index}
                className={styles.seekPrompt}
                onClick={() => {
                  if(index === 0){
                    trackSeekAssetSummariseClickedEvent({assetType : fileType, sourceCta: ASSET_DETAIL})
                    handleAssetSummaryClicked();

                  }
                  else{
                    trackAssetPromptsClickedEvent({assetType : fileType, sourceCta: ASSET_DETAIL})
                    handleQuery(prompt);
                  }
                }}
              >
                {prompt}
              </div>
            ))}
        </div>
      )}
      <SeekChat seekAsset={true} sessionView={false} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleQuery(query);
        }}
      >
        <div
          className={cx(
            styles.seekInputContainer,
            isActive && styles.seekInputActive,
            isSeeking && styles.seeking,
          )}
        >
          <div className={styles.seekInputItems}>
            <Image
              className={cx(styles.seekIcon, isActive && styles.seekIconActive)}
              src="/images/seek/seek.svg"
              alt="Seek"
            />
            <input
              disabled={isSeeking}
              value={query}
              className={styles.seekInput}
              type="text"
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={renderPlaceholder()}
            />
            <i
              className={cx(
                styles.closeIcon,
                query && "paperflite-right-arrow",
              )}
            ></i>
          </div>
        </div>
      </form>
      <div className={styles.actionsBar}>
        <Actions.Container>
          <Actions.Row className={cx(styles.newChat)}>
            <i onClick={() => onClose()} className="paperflite-close"></i>
          </Actions.Row>
        </Actions.Container>
      </div>
    </div>
  );
};

export default SeekAsset;
